import {
    Icon,
    OutlinedCard,
    Text,
    Row,
    Column,
    OverlappingImages,
    JLP_LOGO,
    SANCTUM_LOGO,
    ORCA_LOGO,
    METEORA_LOGO,
    StatRawValue
} from "@bridgesplit/ui";
import { MarketExpanded, useLoopsExpanded, useMarkets, usePrivyWalletConnect } from "@bridgesplit/abf-react";
import { useWallets } from "@privy-io/react-auth/solana";
import { findMaxElement, formatPercent } from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { AppTracking, TrackEvent } from "app/types";

import { AppButton } from "../common";

const ALLOWED_MARKETS = [
    "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
    "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
    "2b1kV6DkPAnxd5ixfnxCpjxmKwqjjaYmCZfHsFu24GXo"
];
export default function LoopscaleWalletSignUpCard() {
    const betaStats = useNeedBetaAccessStats();
    const { connectWallet } = usePrivyWalletConnect();
    const { ready } = useWallets();

    return (
        <OutlinedCard spacing={4} sx={{ textAlign: "center", alignItems: "center" }}>
            <Text svgColor="secondary">
                <Icon type="logo-outer" />
            </Text>
            <Column alignItems={"center"} spacing={1}>
                <Text fontWeight="normal" variant="h3">
                    {`Connect your wallet to earn ${COPY.POINTS_TERM.toLowerCase()}!`}
                </Text>
                <Text variant="body1" color="caption">
                    Experience higher yields, better rates, and expanded collateral options
                </Text>
            </Column>
            <Row sx={{ width: "100%", justifyContent: "space-evenly" }}>
                {betaStats.map((b, i) => (
                    <AccessStat key={`${i}-loopscale-beta-card`} {...b} />
                ))}
            </Row>
            <Column sx={{ width: "100%" }} alignItems={"center"} spacing={1}>
                <AppButton
                    overrideAccess
                    onClick={() => connectWallet()}
                    color="secondary"
                    isTransaction={false}
                    disabled={!ready}
                    tracking={AppTracking.from(TrackEvent.SubmitWalletConnect)}
                >
                    <Icon type="connect-wallet" />
                    {COPY.CONNECT_PROMPT}
                </AppButton>
            </Column>
        </OutlinedCard>
    );
}

function useNeedBetaAccessStats(): AccessStat[] {
    const { data } = useLoopsExpanded({
        showHidden: false
    });
    const { data: markets } = useMarkets();
    const custodians = [ORCA_LOGO, METEORA_LOGO, SANCTUM_LOGO, JLP_LOGO];

    const filteredMarketStats = markets?.filter((m) =>
        ALLOWED_MARKETS.some((allowedMarket) => allowedMarket === m.metadata.mint)
    );

    const lowestApyMarket = findLowestApyMarket(filteredMarketStats);

    const apy = lowestApyMarket ? lowestApyMarket : undefined;

    const loopWithMaxLeveragedApy = findMaxElement(data, (item) => item.loopVault.maxLeveragedApyPct);
    const betaStats: AccessStat[] = [
        {
            value: (
                <Text color="success">{formatPercent(loopWithMaxLeveragedApy?.loopVault.maxLeveragedApyPct)} APY</Text>
            ),
            caption: "Earn up to"
        },
        {
            value: <Text color="body">{`${formatPercent(apy)} APY`}</Text>,
            caption: "Borrow at"
        },
        {
            value: <StackedCustodianImages featuredCustodians={custodians} />,
            caption: "Leverage"
        }
    ];

    return betaStats;
}

function StackedCustodianImages({ featuredCustodians }: { featuredCustodians: string[] | undefined }) {
    return <OverlappingImages size="20px" images={featuredCustodians} maxLength={4} />;
}

function findLowestApyMarket(marketStats: MarketExpanded[] | undefined) {
    if (!marketStats) return undefined;
    return Math.min(...marketStats.flatMap((market) => market.strategyStats.durationToMinApy.map((item) => item.apy)));
}

interface AccessStat {
    value: StatRawValue | [StatRawValue, StatRawValue];
    caption: string;
}
function AccessStat({ caption, value }: AccessStat) {
    return (
        <Column alignItems={"center"}>
            <Text variant="body2" color="caption">
                {caption}
            </Text>
            {value}
        </Column>
    );
}
